import React, { useEffect, useMemo, useState, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { X, Download } from 'lucide-react';
import Drawer from 'react-modern-drawer';
import 'react-modern-drawer/dist/index.css';

import api from '../../../services/api';
import DataTable from '../../../components/ui/DataTable';
import Button from '../../../components/ui/Button';
import PersonCell from '../../../components/ui/PersonCell';

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #E8ECF0;
`;
const DrawerTitle = styled.h3`
  font-size: ${({ theme }) => theme.metrics.font.size.lg};
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: ${({ theme }) => theme.metrics.font.weight.bold};
  margin: 0;
`;
const DrawerCloseBtn = styled.button`
  display: flex; align-items: center; justify-content: center;
  width: 32px; height: 32px;
  border: none; border-radius: 8px; background: none;
  color: ${({ theme }) => theme.colors.text.dark.little};
  cursor: pointer;
  &:hover { background: ${({ theme }) => theme.colors.bgColor}; }
`;
const DrawerBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
`;
const SubInfo = styled.div`
  display: grid;
  grid-template-columns: max-content 1fr;
  column-gap: 12px;
  row-gap: 6px;
  margin-bottom: 24px;
  font-size: 13px;
`;
const SubInfoLabel = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-weight: 600;
`;
const SubInfoValue = styled.div`
  color: ${({ theme }) => theme.colors.text.primary};
  word-break: break-word;
`;
const QuestionBlock = styled.div`
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #F1F3F5;
`;
const QuestionText = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 6px;
`;
const AnswerText = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  white-space: pre-wrap;
`;
const EmptyAnswer = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.light};
  font-style: italic;
`;
const PillList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;
const Pill = styled.span`
  background: ${({ theme }) => theme.colors.primary.light}22;
  color: ${({ theme }) => theme.colors.primary.main};
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
`;

export default function SurveyRespondentsTab({ surveyId, containerHeight }) {
  const { t } = useTranslation();

  const [data, setData] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [queryParams, setQueryParams] = useState({ pageNumber: 1, pageSize: 10 });
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    setFetching(true);
    api.get(`/v1/surveys/${surveyId}/respondents?${new URLSearchParams(queryParams)}`)
      .then((res) => {
        setData(res.data?.data?.data || []);
        setPagination({
          totalRowCount: res.data?.data?.totalRowCount || 0,
          pageSize: res.data?.data?.pageSize || 10,
          pageIndex: (res.data?.data?.pageIndex || 1) - 1,
        });
      })
      .finally(() => setFetching(false));
  }, [surveyId, queryParams]);

  const columns = useMemo(() => ([
    { name: 'fullName', label: t('survey.respondent'), options: {
      customBodyRender: (v, tableMeta) => (
        <PersonCell name={v} profilePicture={data[tableMeta.rowIndex]?.profilePicture} />
      ), filter: false } },
    { name: 'email', label: t('survey.email'), options: { filter: false, customBodyRender: (v) => v || '-' } },
    { name: 'phone', label: t('survey.phone'), options: { filter: false, customBodyRender: (v) => v || '-' } },
    { name: 'organization', label: t('survey.organization'), options: { filter: false, customBodyRender: (v) => v || '-' } },
    {
      name: 'submittedAt',
      label: t('survey.submittedAt'),
      options: { filter: false, customBodyRender: (v) => new Date(v).toLocaleString() },
    },
  ]), [t, data]);

  const handleRowClick = useCallback((row) => {
    if (!row?.id) return;
    setDetailLoading(true);
    setDetail({ id: row.id, loading: true });
    api.get(`/v1/surveys/${surveyId}/respondents/${row.id}`)
      .then((res) => setDetail(res.data?.data || null))
      .finally(() => setDetailLoading(false));
  }, [surveyId]);

  const handleTableChange = useCallback((_, tableState) => {
    setQueryParams({
      pageNumber: (tableState.page || 0) + 1,
      pageSize: tableState.rowsPerPage || 10,
    });
  }, []);

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const res = await api.get(`/v1/surveys/${surveyId}/respondents/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `survey-respondents-${surveyId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {}
    setExporting(false);
  }, [surveyId]);

  return (
    <div style={{ height: '100%', overflow: 'hidden' }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <Button variant="outline" onClick={handleExport} disabled={exporting || data.length === 0}>
          <Download size={14} /> {t('survey.exportRespondents')}
        </Button>
      </div>
      <DataTable
        title={t('survey.respondents')}
        fetching={fetching}
        columns={columns}
        data={data}
        totalRowCount={pagination.totalRowCount}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        onRowClick={handleRowClick}
        onTableChanged={handleTableChange}
        tableBodyMaxHeight={containerHeight ? `${containerHeight - 144}px` : undefined}
      />

      <Drawer
        open={!!detail}
        onClose={() => setDetail(null)}
        direction="right"
        size="45%"
        duration={150}
        overlayOpacity={0.2}
        style={{
          display: 'flex',
          flexDirection: 'column',
          maxWidth: '100vw',
          minWidth: 380,
          margin: '12px',
          borderRadius: '6px',
          height: 'calc(100vh - 24px)',
          boxShadow: '0 8px 32px rgba(0, 66, 66, 0.12)',
        }}
      >
        <DrawerHeader>
          <DrawerTitle>{detail?.fullName || t('survey.respondent')}</DrawerTitle>
          <DrawerCloseBtn onClick={() => setDetail(null)}><X size={20} /></DrawerCloseBtn>
        </DrawerHeader>
        <DrawerBody>
          {detailLoading || !detail?.answers ? (
            <EmptyAnswer>{t('common.loading')}</EmptyAnswer>
          ) : (
            <>
              <SubInfo>
                {detail.email && (<><SubInfoLabel>{t('survey.email')}</SubInfoLabel><SubInfoValue>{detail.email}</SubInfoValue></>)}
                {detail.phone && (<><SubInfoLabel>{t('survey.phone')}</SubInfoLabel><SubInfoValue>{detail.phone}</SubInfoValue></>)}
                {detail.organization && (<><SubInfoLabel>{t('survey.organization')}</SubInfoLabel><SubInfoValue>{detail.organization}</SubInfoValue></>)}
                <SubInfoLabel>{t('survey.submittedAt')}</SubInfoLabel>
                <SubInfoValue>{new Date(detail.submittedAt).toLocaleString()}</SubInfoValue>
              </SubInfo>

              {detail.answers.map((a) => (
                <QuestionBlock key={a.questionId}>
                  <QuestionText>{a.questionText}</QuestionText>
                  {a.selectedOptionLabel ? (
                    <PillList><Pill>{a.selectedOptionLabel}</Pill></PillList>
                  ) : a.selectedOptionLabels?.length > 0 ? (
                    <PillList>{a.selectedOptionLabels.map((l, i) => <Pill key={i}>{l}</Pill>)}</PillList>
                  ) : a.textValue ? (
                    <AnswerText>{a.textValue}</AnswerText>
                  ) : a.alternateText ? (
                    <AnswerText>{a.alternateText}</AnswerText>
                  ) : (
                    <EmptyAnswer>{t('survey.noAnswer')}</EmptyAnswer>
                  )}
                </QuestionBlock>
              ))}
            </>
          )}
        </DrawerBody>
      </Drawer>
    </div>
  );
}
